/**
 * Layer 3 of the manifest: a refusal names the capability it is about. Every
 * code the setup validators give (the template validator and the field rules
 * it runs, the compose reader and judge, the authoring, the formula checker,
 * and the server's link check) maps to a manifest entry, or to null on
 * purpose when the code is about a typo or the wire rather than a capability.
 * apps/server/src/manifest-refusals.enforcement.test.ts keeps the two tables
 * complete: a new validator code fails it until it is mapped here.
 *
 * `refusalNotBuilt` is how the harness words a refused change: "not
 * possible yet" only for an entry RecordOS does not build.
 */
import type { SetupDraft, SetupDraftField } from '../compose-author';
import { isValueTypeId } from '../generated/value-types';
import { LINK_PATTERNS, type LinkPattern } from '../links';
import { studioLimitOf } from '../setup-draft/judge';
import type { STUDIO_LIMITS } from '../setup-draft/draft';
import type { CapabilityId } from './ids';
import { capabilityById } from './manifest';

export type RefusedProblem = { readonly code: string; readonly message: string; readonly path?: string };

const each = <T extends string>(codes: readonly T[], id: CapabilityId | null): Record<T, CapabilityId | null> =>
  Object.fromEntries(codes.map((code) => [code, id])) as Record<T, CapabilityId | null>;

/** The codes whose entry is fixed. Null means the code is about a typo or the wire, not a capability. */
export const REFUSAL_CAPABILITY: Readonly<Record<string, CapabilityId | null>> = {
  // The size of a setup.
  ...each(['RECORDS_TOO_MANY', 'RECORDS_MISSING', 'FIELDS_TOO_MANY', 'FIELDS_MISSING', 'SLOTS_FULL', 'MAIN_RECORD_MISSING', 'WORDS_TOO_MANY', 'RECORD_KIND_INVALID'], 'record.record'),
  ...each(['DIRECTORY_HOLDS_MONEY'], 'record.list'),
  ...each(['ICON_UNKNOWN'], 'record.icon'),
  ...each(['BINDING_FIELD_UNKNOWN', 'BINDING_PURPOSE_DUP', 'BINDING_TITLE_MISSING', 'BINDING_AMBIGUOUS', 'BINDING_EMPTY', 'PURPOSE_UNKNOWN', 'PURPOSE_TYPE_MISMATCH', 'CONST_NOT_ALLOWED', 'CONST_OUT_OF_RANGE'], 'record.bindings'),
  ...each(
    ['SEED_FIELD_CONDITIONAL', 'SEED_FIELD_UNKNOWN', 'SEED_ON_RECORD', 'SEED_REFERENCE_RING', 'SEED_REFERENCE_UNKNOWN', 'SEED_ROWS_TOO_MANY', 'SEED_VALUE_SET'],
    'record.seed_rows',
  ),
  // Fields.
  ...each(['OPTIONS_MISSING', 'OPTIONS_UNEXPECTED', 'OPTION_KEY_DUP'], 'field.type.select'),
  ...each(['OPTION_TONE', 'SIGN_FIELD_MISSING', 'SIGN_OPTION_MISSING'], 'field.option.option_sign_tone'),
  ...each(['SHAPE_UNKNOWN', 'COMPONENT_UNKNOWN', 'COMPONENT_TYPE_MISMATCH', 'PROP_TYPE', 'PROP_ENUM', 'PROP_UNKNOWN'], 'field.option.shape'),
  ...each(['REQUIRED_IN_ATTRS'], 'field.option.required'),
  ...each(['REFERENCE_TARGET_NOT_DIRECTORY', 'REFERENCE_TARGET_SELF', 'REFERENCE_TARGET_UNKNOWN', 'REFERENCE_NEEDS_TARGET'], 'field.type.reference'),
  ...each(
    ['FILTER_CYCLE', 'FILTER_EQUALS_NOT_CHOICE', 'FILTER_EQUALS_UNKNOWN', 'FILTER_FIELD_NOT_REFERENCE', 'FILTER_FIELD_UNKNOWN', 'FILTER_LIST_MISMATCH', 'FILTER_NOT_CHOICE', 'FILTER_SHAPE'],
    'field.option.filter',
  ),
  ...each(['FILTER_MAX_CHOSEN'], 'field.option.max_chosen'),
  ...each(
    [
      'LINES_COUNT_FROM',
      'LINES_FIELDS_EMPTY',
      'LINES_FIELDS_MAX',
      'LINES_KEY_RESERVED',
      'LINES_MAX_SHAPE',
      'LINES_NEVER_REQUIRED',
      'LINES_NOT_ALLOWED',
      'LINES_NO_DEFAULT',
      'LINES_ON_LIST',
      'LINES_SHAPE',
      'TARGET_NOT_ALLOWED',
    ],
    'field.type.lines',
  ),
  ...each(['LINES_TOO_DEEP'], 'not_yet.third_lines_level'),
  // Worked-out values: the checker's own codes, and the template validator's.
  ...each(
    [
      'FORMULA_SHAPE',
      'FORMULA_TOO_BIG',
      'FORMULA_FIELD_UNKNOWN',
      'FORMULA_FIELD_COMPUTED',
      'FORMULA_FIELD_UNREADABLE',
      'FORMULA_PARENT_COMPUTED',
      'FORMULA_CYCLE',
      'FORMULA_NODE_DUPLICATE',
      'FORMULA_NODE_UNKNOWN',
      'FORMULA_NODE_UNUSED',
      'FORMULA_ROOT_UNKNOWN',
      'FORMULA_TYPE_MISMATCH',
      'FORMULA_RESULT_MISMATCH',
      'FORMULA_NEVER_REQUIRED',
      'FORMULA_SPEC_MISSING',
      'FORMULA_DEEP',
    ],
    'field.type.formula',
  ),
  ...each(['FORMULA_HOP_NOT_REFERENCE', 'FORMULA_HOP_NOT_STORED'], 'formula.piece.hop'),
  ...each(['FORMULA_AGG_NOT_HERE'], 'formula.piece.agg'),
  // Where fields sit.
  ...each(['LAYOUT_FIELD_TWICE', 'LAYOUT_FIELD_UNKNOWN', 'LAYOUT_NODE_UNKNOWN', 'LAYOUT_SHAPE', 'LAYOUT_TOO_BIG', 'LAYOUT_TOO_DEEP'], 'arrangement.sections'),
  // How entries are written, and the cards on top.
  ...each(
    ['CAPTURE_CELL_NOT_SELECT', 'CAPTURE_FIELD_UNKNOWN', 'CAPTURE_MODE', 'CAPTURE_NOT_OBJECT', 'CAPTURE_OVER_NOT_REFERENCE', 'CAPTURE_QUICK_UNKNOWN', 'CAPTURE_SHARED_INVALID'],
    'surface.sheet',
  ),
  ...each(['SUMMARY_CARD_UNKNOWN', 'SUMMARY_COUNT_INVALID', 'SUMMARY_TILE_NOT_OBJECT', 'TILE_FIELD_MISSING'], 'view.summary'),
  ...each(['SUMMARY_GROUP_BY_MISSING', 'SUMMARY_GROUP_BY_UNKNOWN'], 'insight.group_by'),
  ...each(['SUMMARY_SCOPE_IMPOSSIBLE'], 'insight.scope'),
  ...each(
    [
      'TILE_CONDITION_FIELD_UNKNOWN',
      'TILE_CONDITION_FIELD_UNREADABLE',
      'TILE_CONDITION_NOT_OBJECT',
      'TILE_CONDITION_NUM_INVALID',
      'TILE_CONDITION_NUM_MISSING',
      'TILE_CONDITION_OPTION_UNKNOWN',
      'TILE_CONDITION_OP_INVALID',
      'TILE_WHERE_NOT_LIST',
    ],
    'insight.where',
  ),
  // Links: the setup judge's, and the server's.
  ...each(['LINKS_TOO_MANY'], 'link.and'),
  ...each(['LINK_SAME_RECORD', 'TARGET_SAME_RECORD', 'CYCLE'], 'link.pattern.feed'),
  ...each(['LINK_CARRY_EMPTY', 'TYPE_MISMATCH', 'REQUIRED_UNFILLED', 'LINK_FILL_REQUIRED', 'LINK_FILL_CONST', 'LINK_FILL_COMPUTED'], 'link.fill.field'),
  ...each(['LINES_RECORD'], 'link.reads_lines'),
  ...each(['LINK_VIA_NOT_REFERENCE'], 'link.via_condition'),
  ...each(['LINK_FILL_TYPE', 'LINK_FILL_LIST'], 'link.fill.reference'),
  ...each(['LINK_FORMULA_LINES'], 'link.fill.formula'),
  ...each(['TARGET_MODE_UNSUPPORTED'], 'link.match_open_row'),
  // The workspace.
  ...each(['CURRENCY_MISSING', 'CURRENCY_UNKNOWN', 'CURRENCY_NOT_REQUIRED_BUT_MONEY', 'CURRENCY_REQUIRED_BUT_NO_MONEY'], 'workspace.currency'),
  // A typo, the wire, or a template's own bookkeeping: no capability to name.
  ...each(
    [
      'BUSINESS_NAME_INVALID',
      'SETUP_NOT_OBJECT',
      'SPEC_INVALID',
      'SPEC_NOT_OBJECT',
      'SPEC_ID_NOT_COMPOSED',
      'SPEC_VERSION_NOT_ONE',
      'SPEC_UNEXPECTED',
      'WORDS_INVALID',
      'WORD_INVALID',
      'WORD_KEY_INVALID',
      'WORD_SHADOWS_CATALOGUE',
      'LABEL_KEY_MISSING',
      'LABEL_KEY_UNKNOWN',
      'NOUN_KEY_NOT_SHIPPED',
      'LINKS_INVALID',
      'LINK_SHAPE',
      'LINK_FIELD_UNKNOWN',
      'LINK_OPTION_UNKNOWN',
      'FIELD_UNKNOWN',
      'TARGET_UNKNOWN',
      'RECORD_KEY_DUP',
      'RECORD_KEY_INVALID',
      'RECORD_NOT_OBJECT',
      'FIELD_KEY_DUP',
      'FIELD_LABEL_DUP',
      'FIELD_KEY_INVALID',
      'FIELD_NOT_OBJECT',
      'BINDING_NOT_OBJECT',
      'TEMPLATE_ID_INVALID',
      'TEMPLATE_VERSION_INVALID',
      'TIER_INVALID',
      'TIER_NOT_CUSTOM',
      'ORD_INVALID',
      'ORD_TAKEN',
      'VIEW_MISSING',
      'VIEW_NOT_OBJECT',
      'VIEW_ORD_TAKEN',
      'VIEW_TYPE_INVALID',
      'ATTR_KEY_INVALID',
      'ATTR_KEY_MISSING',
      'ATTR_KEY_TAKEN',
      'SLOT_MISSING',
      'SLOT_TAKEN',
      'SLOT_UNKNOWN',
      'SLOT_FAMILY',
      'STORAGE_TARGET',
      'SAMPLE_COUNT',
      'SAMPLE_FIELD_UNKNOWN',
      'QUESTIONNAIRE_NOT_LIST',
      'CHIP_EMPTY',
      'CHIP_ID_DUP',
      'CHIP_ID_INVALID',
      'CHIP_TEMPLATE_UNKNOWN',
      'ANSWER_TAGS_INVALID',
      'ANSWER_TAG_UNKNOWN',
      'KEY_BY_INVALID',
      'RECORD_WHEN_ANSWER_NOT_DIRECTORY',
      'SEED_WHEN_NOT_OBJECT',
      'SEED_WHEN_VALUES_MISSING',
      'WHEN_ANSWER_ON_BOUND',
      'WHEN_ANSWER_ON_CAPTURE',
      'WHEN_ANSWER_ON_REQUIRED',
      'WHEN_ANSWER_ON_VIEW_FIELD',
      'WHEN_ANSWER_PAIR_BROKEN',
      'LAYOUT_VERSION_NEW',
    ],
    null,
  ),
};

/* ----------------------------------------------- codes read from the problem */

/** The link a problem's path names in the draft: `composed.links[2]`. */
const linkOf = (problem: RefusedProblem, draft: SetupDraft | undefined) => {
  const index = /\.links\[(\d+)\]/.exec(problem.path ?? '')?.[1];
  return index === undefined ? undefined : draft?.links?.[Number(index)];
};

const fieldsDeep = (fields: readonly SetupDraftField[]): SetupDraftField[] =>
  fields.flatMap((field) => [field, ...fieldsDeep(field.lines?.fields ?? [])]);

/** The field a problem's path names in the draft: `composed.records[sales].fields[worked]`, lines included. */
const fieldOf = (problem: RefusedProblem, draft: SetupDraft | undefined): SetupDraftField | undefined => {
  const path = problem.path ?? '';
  const recordKey = /\.records\[([^\]]+)\]/.exec(path)?.[1];
  const fieldKeys = [...path.matchAll(/fields\[([^\]]+)\]/g)].map((match) => match[1]!);
  const fieldKey = fieldKeys.at(-1);
  if (recordKey === undefined || fieldKey === undefined) return undefined;
  const record = draft?.records.find((one) => one.key === recordKey);
  return record === undefined ? undefined : fieldsDeep(record.fields).find((field) => field.key === fieldKey);
};

const patternEntry = (problem: RefusedProblem, draft?: SetupDraft): CapabilityId => {
  const pattern = linkOf(problem, draft)?.pattern as string | undefined;
  return pattern !== undefined && (LINK_PATTERNS as readonly string[]).includes(pattern) ? `link.pattern.${pattern as LinkPattern}` : 'link.and';
};

const typeEntry = (problem: RefusedProblem, draft?: SetupDraft): CapabilityId | null => {
  const type = fieldOf(problem, draft)?.type as string | undefined;
  return type !== undefined && isValueTypeId(type) ? `field.type.${type}` : null;
};

/** The codes whose entry depends on the problem: what its path names in the draft. */
/** The capability each ceiling of the studio draft is about. */
const STUDIO_LIMIT_CAPABILITY: Partial<Record<keyof typeof STUDIO_LIMITS, CapabilityId>> = {
  records: 'record.record',
  fields: 'record.record',
  links: 'link.and',
  seedRows: 'record.list',
  boardCards: 'insight.dashboard',
  roles: 'people.role',
  rolePeople: 'people.role',
  imports: 'input.import',
};

export const REFUSAL_CAPABILITY_OF: Readonly<Record<string, (problem: RefusedProblem, draft?: SetupDraft) => CapabilityId | null>> = {
  LINK_PATTERN_NO_ENGINE: patternEntry,
  PATTERN_UNSUPPORTED: patternEntry,
  // A field of a type no setup places (rollup, lookup): the registry's storage rule refuses it.
  STORAGE_MISMATCH: typeEntry,
  VALUE_TYPE_UNKNOWN: typeEntry,
  // "toRecordKey "x" is not a record of this spec": a list in the draft is a link into a list; anything else is a typo.
  LINK_RECORD_UNKNOWN: (problem, draft) => {
    const key = /"([^"]+)"/.exec(problem.message)?.[1];
    const named = key === undefined ? undefined : draft?.records.find((record) => record.key === key);
    return named?.kind === 'directory' ? 'link.into_list' : null;
  },
  // The studio judge's ceilings (setup-draft/judge.ts): the message names which.
  LIMIT: (problem) => {
    const limit = studioLimitOf(problem);
    return limit === null ? null : (STUDIO_LIMIT_CAPABILITY[limit] ?? null);
  },
};

/** The entry a problem is about, or null when it is about none. */
export const refusalCapability = (problem: RefusedProblem, draft?: SetupDraft): CapabilityId | null => {
  const of = REFUSAL_CAPABILITY_OF[problem.code];
  if (of !== undefined) return of(problem, draft);
  return REFUSAL_CAPABILITY[problem.code] ?? null;
};

/**
 * Whether a refusal is about something RecordOS does not build yet (its
 * entry is not shipped), something it builds once the change is made another
 * way (a shipped entry: a link that fills a field, a field of a type), or
 * names no entry (null). Never words: the person reads "not possible" only
 * for an entry not built (D.2 of docs/plans/links-fixes.md, 17 Oct: "not
 * possible: see link.fill.field" on a link that three named fixes would draw).
 */
export const refusalNotBuilt = (problem: RefusedProblem, draft?: SetupDraft): boolean | null => {
  const id = refusalCapability(problem, draft);
  const entry = id === null ? null : capabilityById(id);
  return entry === null ? null : entry.status !== 'shipped';
};
